/**
 * Wire ⇄ engine adapter — the ONLY place canonical wire shapes (cloud `VersionPayload`,
 * see docs/INTEGRATION-CONTRACT.md) are converted to the genz-engine's internal serde
 * shapes (Rust). Resolves the cross-component seams F2–F5 from the audit:
 *   - polygon `[[x,y]]` (wire) ⇄ `{points:[{x,y}]}` (engine)
 *   - one kind-tagged `masks[]` (wire) → split `print_area` + `cutouts[]` (engine)
 *   - `output_size`/`formats[]` (wire) → `output_width/height`/`formats[]` (engine)
 *   - status `Draft|Pending|Active` (wire) ⇄ `draft|pending_approval|active` (engine)
 *   - alpha is `alpha_ref` on the wire (no pixels); engine inline alpha is resolved locally at render.
 */
import type {
  WireMask,
  WirePolygon,
  WireRenderSettings,
  WireStatus,
  WireTemplateVersion,
  WireView,
} from "./wire.js";
import type { Mask, Polygon } from "./types.js";
import { normalizeMasksForWire } from "./legacy.js";

// --- engine-side shapes this adapter emits (mirror genz-engine serde) ---
export interface EnginePoint {
  x: number;
  y: number;
}
export interface EnginePolygon {
  points: EnginePoint[];
}
/** Every `MaskKind` the engine's serde will accept, in its own spelling. Exported because the
 *  floor below must guard on MEMBERSHIP of this set, not on a truthy string. */
export const ENGINE_MASK_KINDS = ["print_area", "camera_cutout", "border", "button"] as const;
export type EngineMaskKind = (typeof ENGINE_MASK_KINDS)[number];
export function isEngineMaskKind(k: unknown): k is EngineMaskKind {
  return typeof k === "string" && (ENGINE_MASK_KINDS as readonly string[]).includes(k);
}
export interface EngineMask {
  kind: EngineMaskKind;
  polygon: EnginePolygon;
  alpha: null; // inline raster resolved locally at render; never carried on the wire
}
export interface EngineLayer {
  kind: "shadow" | "reflection" | "gloss" | "emboss";
  opacity: number;
  blur: number;
  region: EnginePolygon | null;
  /** Optional raster coverage (e.g. a resolved brand-logo alpha for an emboss layer);
   *  when present it drives the effect instead of `region` (spec 015 / B4).
   *  ENG-9: `wireViewToEngine` never populates this (the wire carries no pixels). The ONLY
   *  producer is `apps/shared-ui/src/render/raster.ts::resolveBrandEmboss`, which is itself
   *  unreferenced outside its own test — so this path is dead end-to-end today. Kept only
   *  because deleting it would break that (shared-ui-owned) file's typecheck; it should be
   *  removed together with `raster.ts` + `raster.test.ts`. The Rust `Layer.alpha` is real
   *  and tested (`render::compose::scale_alpha`), so nothing is lost by re-adding it. */
  alpha?: { width: number; height: number; alpha: number[] } | null;
}
export type EngineView = "back" | "front" | "angle" | "isometric";
export interface EngineAssetSet {
  view: EngineView;
  base_image_ref: string;
  print_area: EngineMask;
  cutouts: EngineMask[];
  layers: EngineLayer[];
  perspective_points: [EnginePoint, EnginePoint, EnginePoint, EnginePoint];
  /** Spec 017 US6: optional 4 normalized corners where the brand logo is composited. */
  brand_logo_placement?: [EnginePoint, EnginePoint, EnginePoint, EnginePoint] | null;
  output_crop: EnginePolygon;
  approximate: boolean;
}
export type EngineStatus = "draft" | "pending_approval" | "active" | "rejected";
export interface EngineRenderSettings {
  formats: ("jpg" | "png" | "webp")[];
  output_width: number;
  output_height: number;
  jpg_quality: number;
  fit_policy: "contain" | "cover" | "stretch" | "height";
  print_dpi: number;
  background: [number, number, number, number];
  /** Optional vertical gradient background [top, bottom] (spec 015 / B2). */
  background_gradient?: [
    [number, number, number, number],
    [number, number, number, number],
  ] | null;
  // ENG-9: the spec-015 `background_image` field was declared here and never set by anything
  // — the wire is pixel-free by contract (Constitution VII), so the adapter can never source
  // it, and no local caller populated it either. Removed rather than left as a field that
  // looks wired. The Rust `RenderSettings.background_image` stays (it defaults to `None` and
  // is `skip_serializing_if`), so re-adding it is a one-line change if a local background-
  // image feature is ever built. See specs/015-*.
}
export interface EngineTemplateVersion {
  id: string;
  template_id: string;
  version: number;
  status: EngineStatus;
  locked: boolean;
  views: EngineAssetSet[];
  settings: EngineRenderSettings;
}

const FULL_FRAME: WirePolygon = [
  [0, 0],
  [1, 0],
  [1, 1],
  [0, 1],
];

// --- status -------------------------------------------------------------
const WIRE_TO_ENGINE_STATUS: Record<WireStatus, EngineStatus> = {
  Draft: "draft",
  Pending: "pending_approval",
  Active: "active",
};

export function wireStatusToEngine(s: WireStatus): EngineStatus {
  // Null-safe (ENG-9): the one lookup the v0.2.62 null-safety pass missed. A status the
  // cloud adds later (or a partially-synced row with no status at all) returned `undefined`
  // here, which serde then rejected with an opaque "invalid value" deep inside the engine
  // rather than at the boundary. "draft" is the safe floor — it is never renderable, so a
  // wrong guess fails closed instead of rendering an unapproved template.
  return WIRE_TO_ENGINE_STATUS[s] ?? "draft";
}

// --- output-size clamps (ENG-1) -----------------------------------------------------
//
// `output_size` / `print_dpi` come off the wire unbounded and land in the engine's canvas
// allocation. The Rust side now REFUSES anything out of range (see
// `engine/genz-engine/src/render/mod.rs`), because on wasm an oversized allocation traps
// and kills the render worker for the whole browser session. Here — the wire→engine seam,
// one layer earlier — we CLAMP instead of throwing, so a template with a silly size still
// renders at a sane size rather than failing the batch. Keep these in step with the Rust
// constants; the Rust guard is the backstop for anything that reaches the engine another way.

/** Largest output edge in pixels (mirrors Rust `render::MAX_OUTPUT_DIM`). */
export const MAX_OUTPUT_DIM = 16_384;
/** Largest total output area in pixels (mirrors Rust `render::MAX_OUTPUT_PIXELS`). */
export const MAX_OUTPUT_PIXELS = 64_000_000;
/** Largest print DPI (mirrors Rust `render::MAX_PRINT_DPI`). */
export const MAX_PRINT_DPI = 1_200;

/** One dimension → a positive integer at or below [`MAX_OUTPUT_DIM`]; junk → `fallback`. */
function clampDim(v: unknown, fallback: number): number {
  if (typeof v !== "number" || !Number.isFinite(v) || v < 1) return fallback;
  return Math.min(Math.round(v), MAX_OUTPUT_DIM);
}

/**
 * Clamp a requested output size to something the engine will actually allocate: each side
 * to [`MAX_OUTPUT_DIM`], then — if the area still exceeds [`MAX_OUTPUT_PIXELS`] — scale BOTH
 * sides down by the same factor, so the aspect ratio (and therefore the framing of the
 * mockup) survives the clamp.
 */
export function clampOutputSize(
  width: unknown,
  height: unknown,
  fallback: [number, number] = [1200, 1200],
): [number, number] {
  let w = clampDim(width, fallback[0]);
  let h = clampDim(height, fallback[1]);
  if (w * h > MAX_OUTPUT_PIXELS) {
    const scale = Math.sqrt(MAX_OUTPUT_PIXELS / (w * h));
    w = Math.max(1, Math.floor(w * scale));
    h = Math.max(1, Math.floor(h * scale));
  }
  return [w, h];
}

/** Clamp a print DPI into `1..=MAX_PRINT_DPI`; junk → `fallback`. */
export function clampPrintDpi(dpi: unknown, fallback = 300): number {
  if (typeof dpi !== "number" || !Number.isFinite(dpi) || dpi < 1) return fallback;
  return Math.min(Math.round(dpi), MAX_PRINT_DPI);
}

export function engineStatusToWire(s: EngineStatus): WireStatus {
  switch (s) {
    case "active":
      return "Active";
    case "pending_approval":
      return "Pending";
    case "draft":
    case "rejected": // a rejected version returns to Draft (cloud system-of-record)
      return "Draft";
  }
}

// --- geometry -----------------------------------------------------------
export function wirePolygonToEngine(p: WirePolygon | null | undefined): EnginePolygon {
  // Null-safe: templates authored through the AI-matte flow can carry masks whose polygon
  // was never set (alpha-only). A missing polygon means "the whole frame", never a crash —
  // an un-hydrated device falling back to the classic path used to TypeError here and kill
  // the entire batch job ("Cannot read properties of undefined").
  return { points: (p && p.length > 0 ? p : FULL_FRAME).map(([x, y]) => ({ x, y })) };
}

export function enginePolygonToWire(p: EnginePolygon): WirePolygon {
  return p.points.map((pt) => [pt.x, pt.y]);
}

// --- TS-domain (Point[]) ⇄ engine converters (shared by the wasm + native bindings) ---
export const tsPolygonToEngine = (p: Polygon): EnginePolygon => ({ points: p.map((pt) => ({ x: pt.x, y: pt.y })) });
export const enginePolygonToTs = (ep: EnginePolygon): Polygon => (ep?.points ?? []).map((pt) => ({ x: pt.x, y: pt.y }));
export const tsMaskToEngine = (m: Mask): EngineMask => ({
  kind: (m.kind ?? "camera_cutout") as EngineMaskKind,
  polygon: tsPolygonToEngine(m.polygon),
  alpha: null,
});

function wireMaskToEngine(m: WireMask, index: number, viewType: string): EngineMask {
  // F-4: `kind` is the engine's `MaskKind` ENUM, and the floor guards on MEMBERSHIP of it —
  // not on "is this string truthy". Two different inputs, two different answers:
  //
  //   ABSENT  — `undefined` crosses as a serde unit value and fails the WHOLE render with
  //             "invalid type: unit value, expected enum MaskKind". `wireViewToEngine`
  //             normalises the legacy `mask_type` spelling before this point, so the only way
  //             to get here without a kind is a record that never had one. "camera_cutout" is
  //             the same safe floor `tsMaskToEngine` uses — a subtracted region renders
  //             conservatively, where guessing "print_area" would print the design over the
  //             whole case.
  //   PRESENT but not a member — a near-miss like "cutout" used to sail straight past a
  //             truthiness check and die deep in the engine as an opaque serde string. Guessing
  //             is worse than failing: flooring it to "camera_cutout" would silently subtract a
  //             region the operator never drew. Refuse HERE instead, naming the mask, what it
  //             said and what was expected, so the batch failure is actionable. The message is
  //             deliberately deterministic-looking to `isRetryableRenderError` — an answer
  //             about the input must not be retried three times to reach the same answer.
  const kind = m.kind ?? "camera_cutout";
  if (!isEngineMaskKind(kind)) {
    throw new Error(
      `invalid mask: view "${viewType}" mask ${index} has an unknown kind ${JSON.stringify(kind)} — ` +
        `expected one of ${ENGINE_MASK_KINDS.join(", ")}`,
    );
  }
  return { kind, polygon: wirePolygonToEngine(m.polygon), alpha: null };
}

const VIEW_MAP: Record<WireView["view_type"], EngineView> = {
  back: "back",
  front: "front",
  isometric: "isometric",
  left: "angle",
  right: "angle",
};

function outputCropToEngine(crop: number[] | null | undefined): EnginePolygon {
  if (!crop || crop.length < 4) return wirePolygonToEngine(FULL_FRAME);
  const [x, y, w, h] = crop;
  return wirePolygonToEngine([
    [x, y],
    [x + w, y],
    [x + w, y + h],
    [x, y + h],
  ]);
}

function perspectiveToEngine(p: WirePolygon | null | undefined): [EnginePoint, EnginePoint, EnginePoint, EnginePoint] {
  const pts = (p && p.length === 4 ? p : FULL_FRAME).map(([x, y]) => ({ x, y }));
  return [pts[0], pts[1], pts[2], pts[3]];
}

export function wireViewToEngine(v: WireView): EngineAssetSet {
  // F-4: normalise the LEGACY `{mask_type, geometry:{kind:"rect"}}` spelling before reading
  // `kind`/`polygon`. This is the READ seam's half of the same rule the WRITE seam applies
  // (`@genz/platform`'s version PATCH) — one normaliser, `./legacy.js`, for both. Without it
  // an un-migrated template lost its print area (nothing matched `kind === "print_area"`, so
  // every view fell back to the full frame) and every mask crossed with `kind: undefined`.
  const masks = (normalizeMasksForWire(v.masks ?? []) ?? []) as WireMask[]; // AI-matte-flow views may ship without classic masks
  // Converted BEFORE the split, so the index in the error message is the mask's position in the
  // STORED array — which is what an operator (or a support ticket) can actually look up. The
  // split is unchanged: an absent kind floors to `camera_cutout`, which is not `print_area`.
  const engineMasks = masks.map((m, i) => wireMaskToEngine(m, i, String(v.view_type ?? "")));
  const printMask = engineMasks.find((m) => m.kind === "print_area");
  const cutouts = engineMasks.filter((m) => m.kind !== "print_area");
  return {
    view: VIEW_MAP[v.view_type] ?? "back",
    base_image_ref: v.base_case_image_ref ?? "",
    print_area: printMask ?? { kind: "print_area", polygon: wirePolygonToEngine(FULL_FRAME), alpha: null },
    cutouts,
    layers: (v.layers ?? []).map((l) => ({
      kind: l.kind,
      opacity: typeof l.params?.opacity === "number" ? (l.params.opacity as number) : 0.3,
      blur: typeof l.params?.blur === "number" ? (l.params.blur as number) : 0.0,
      region: null,
    })),
    perspective_points: perspectiveToEngine(v.perspective_points),
    // Spec 017 US6: pass the optional brand-logo placement quad through to the engine.
    brand_logo_placement: v.brand_logo_placement
      ? perspectiveToEngine(v.brand_logo_placement)
      : null,
    output_crop: outputCropToEngine(v.output_crop),
    approximate: v.approximate ?? false,
  };
}

// --- background (F-2) ---------------------------------------------------
//
// `render_settings.background` is the per-template Background choice KEY — one of
// `transparent | solid | gradient | image | brand_color` (cloud `BUILTIN_BACKGROUNDS`), NOT a
// colour. The adapter used to ignore it entirely and hardcode white, so the RENDER leg of that
// setting's round trip did not exist (docs/release-audit-checklist.md).
//
// WS-BG closed that. `transparent` is expressible by the KEY alone (it names an empty canvas).
// `solid` and `brand_color` name a colour only the CLIENT can resolve — the operator's swatch
// and the brand's `color_primary` — so the save path resolves them and stores the pixels in
// `render_settings.background_color` (cloud `RenderSettings.background_color`, added in the same
// release: an OLDER cloud drops the key, which is why the deploy note requires the redeploy).
// This reads that field back.
// `gradient` is carried by `background_gradient` when a payload has one; `image` needs a raster
// the pixel-free wire cannot carry (Constitution VII), so it keeps the white canvas.

/** The canvas colour the engine uses when a template stores no background (sRGB RGBA). */
export const DEFAULT_ENGINE_BACKGROUND: [number, number, number, number] = [255, 255, 255, 255];
/** A fully transparent canvas — what the `transparent` Background choice means. */
export const TRANSPARENT_ENGINE_BACKGROUND: [number, number, number, number] = [0, 0, 0, 0];

/**
 * Output formats that carry an alpha channel. JPEG does not: the engine's encoder flattens by
 * DROPPING the alpha channel (`engine/genz-engine/src/render/export.rs:49-51` — `to_rgb8()` on
 * the RGBA canvas), which turns a transparent pixel into BLACK, not white.
 */
export function formatCarriesAlpha(format: string | null | undefined): boolean {
  return String(format ?? "").toLowerCase() !== "jpg";
}

/**
 * F-1: the background an output of `format` should actually be rendered on.
 *
 * A transparent canvas is only meaningful where the container can store alpha. Asking for one
 * on JPG shipped a BLACK square — and three of the four built-in marketplace presets are JPG
 * (`apps/shared-ui/src/settings/output.ts:42-44`), with the wizard writing `formats` and
 * `background` in the SAME render-settings PATCH. Flattening onto white here (rather than in
 * the Rust encoder) keeps the rule where the REQUESTED format is known — see
 * `engine-shared.ts::buildEngineRenderSettings` — because the format is per OUTPUT, not per
 * template: one export can render the same template to a JPG preset and a PNG preset, and each
 * must get the background its container can hold. A Rust-side flatten could not see that
 * difference without being handed the same decision anyway, and would change the encoder's
 * contract for every other caller.
 */
export function backgroundForFormat(
  background: [number, number, number, number],
  format: string | null | undefined,
): [number, number, number, number] {
  if (formatCarriesAlpha(format) || background[3] === 255) return background;
  // Composite the requested colour over white at its own alpha, so a partially transparent
  // background still lands where the operator asked rather than snapping to plain white.
  const a = Math.max(0, Math.min(255, background[3])) / 255;
  const flat = (c: number) => Math.round(c * a + 255 * (1 - a));
  return [flat(background[0]), flat(background[1]), flat(background[2]), 255];
}

/** A stored `background_color` as a validated engine colour, or null when there is none.
 *  A short/long/out-of-range array is JUNK, not a colour: fall back to the default rather than
 *  painting whatever happened to be in the payload. The cloud validates on write, but a payload
 *  can predate the validator or arrive from a legacy record. */
function storedColor(c: readonly number[] | null | undefined): [number, number, number, number] | null {
  if (!c || c.length !== 4) return null;
  if (!c.every((v) => Number.isFinite(v) && v >= 0 && v <= 255)) return null;
  return [Math.round(c[0]), Math.round(c[1]), Math.round(c[2]), Math.round(c[3])];
}

/**
 * The stored Background choice key (+ its resolved colour) → the engine's
 * `RenderSettings.background` (sRGB RGBA).
 *
 *  * `transparent` → a fully transparent canvas. The JPG rule in `backgroundForFormat` applies
 *    to THIS case only — a container without alpha cannot hold it.
 *  * `solid` / `brand_color` → the stored `background_color` (WS-BG). Absent (an older payload,
 *    or a brand with no colour) → the white default, exactly as before.
 *  * anything else → the white default.
 */
export function wireBackgroundToEngine(
  background: string | null | undefined,
  backgroundColor?: readonly number[] | null,
): [number, number, number, number] {
  if (background === "transparent") return [...TRANSPARENT_ENGINE_BACKGROUND];
  if (background === "solid" || background === "brand_color") {
    return storedColor(backgroundColor) ?? [...DEFAULT_ENGINE_BACKGROUND];
  }
  return [...DEFAULT_ENGINE_BACKGROUND];
}

// --- render settings ----------------------------------------------------
export function wireRenderSettingsToEngine(s: WireRenderSettings | null | undefined): EngineRenderSettings {
  // Null-safe: a summary-only/partially-synced version carries no render_settings — use the
  // engine's own defaults instead of TypeError-ing the whole batch job.
  // ENG-1: the size/DPI are clamped here, at the wire→engine seam, so a template carrying an
  // absurd `output_size` renders small instead of asking the engine for a multi-GB canvas.
  const [output_width, output_height] = clampOutputSize(s?.output_size?.[0], s?.output_size?.[1]);
  return {
    formats: s?.formats ?? ["png"],
    output_width,
    output_height,
    jpg_quality: 90,
    fit_policy: s?.fit_policy ?? "contain",
    print_dpi: clampPrintDpi(s?.print_dpi),
    background: wireBackgroundToEngine(s?.background, s?.background_color),
    // A gradient WINS over the solid colour in the engine (`RenderSettings::background_at`), so
    // a "transparent" template must not keep one — it would re-paint the canvas the operator
    // asked to leave empty.
    background_gradient: s?.background === "transparent" ? null : (s?.background_gradient ?? null),
  };
}

// --- top-level ----------------------------------------------------------
/**
 * Build the engine's `TemplateVersion` JSON from a canonical wire version. This is what
 * the render path must hand the engine (resolving audit F5 — the engine needs the full
 * geometry, not just an id).
 */
export function wireToEngineTemplateVersion(v: WireTemplateVersion): EngineTemplateVersion {
  return {
    id: v.id,
    template_id: v.template_id,
    version: v.version,
    status: wireStatusToEngine(v.status),
    locked: v.locked,
    views: (v.payload?.views ?? []).map(wireViewToEngine),
    settings: wireRenderSettingsToEngine(v.payload?.render_settings),
  };
}
